// src/features/analytics/components/ConcurrencyTrendCard.tsx

import { Paper, Skeleton, Text, Title } from '@mantine/core';
import { AreaChart } from '@mantine/charts';
import { DateTime } from 'luxon';
import type { DayZone, UtcRange } from '@/shared/utils/utcRange';
import { CONCURRENCY_MAX_CHART_POINTS } from '@/lib/constants/analytics';
import { axisTickLabel, fillConcurrencyGaps, hourTicks, instantLabel, snapWindowToHours, windowSpansDays, type EventWindow } from '../eventWindow';
import { AnalyticsUtils } from '../AnalyticsUtils';
import { AnalyticsCoverageNotice } from './AnalyticsCoverageNotice';
import { AnalyticsErrorState } from './AnalyticsErrorState';
import type { AnalyticsEnvelope, ConcurrencyData } from '../types';

interface ConcurrencyTrendCardProps {
  concurrency: AnalyticsEnvelope<ConcurrencyData> | null;
  range: UtcRange;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  onRetry: () => void;
  /** Plain line for a loaded-but-empty range inside coverage (never a blank chart box). */
  emptyCopy?: string;
  /**
   * Stream-page mode (ADR-009 amendment): the effective window is the x
   * domain (epoch seconds, hour ticks shared with "Views by hour") and the
   * series is gap-filled at its interval from the window start. Absent →
   * the category axis Overview has always used, byte-for-byte.
   */
  window?: EventWindow;
  /** Zone for axis and tooltip labels when `window` is set. */
  zone?: DayZone;
}

function ConcurrencyTooltip({ payload, zone }: { payload?: unknown; zone: DayZone }) {
  const rows = payload as { payload?: { bucket: number; Peak: number; Average: number } }[] | undefined;
  const row = rows?.[0]?.payload;
  if (!row) return null;
  return (
    <Paper withBorder shadow="sm" p="xs">
      <Text size="xs" fw={600}>
        {instantLabel(row.bucket, zone)} (local)
      </Text>
      <Text size="xs">Peak: {row.Peak}</Text>
      <Text size="xs">Average: {row.Average}</Text>
    </Paper>
  );
}

/** Minute/hour buckets are points in time — labeled in the viewer's LOCAL timezone (ADR-003 §4). */
function bucketLabel(bucketSeconds: number, interval: 'minute' | 'hour'): string {
  const dt = DateTime.fromSeconds(bucketSeconds);
  return interval === 'hour' ? dt.toFormat('MMM d, ha') : dt.toFormat('MMM d, HH:mm');
}

export function ConcurrencyTrendCard({
  concurrency,
  range,
  isLoading,
  isError,
  error,
  onRetry,
  emptyCopy = 'No viewing data in this range.',
  window,
  zone = 'local',
}: ConcurrencyTrendCardProps) {
  const noDataYet = isError && AnalyticsUtils.isNoDataYetError(error, range);
  const points = concurrency?.data.points ?? [];
  const interval = concurrency?.data.interval ?? 'hour';
  const domain = window ? snapWindowToHours(window) : null;
  const multiDay = domain ? windowSpansDays(domain, zone) : false;
  const series = domain ? fillConcurrencyGaps(points, domain, interval, CONCURRENCY_MAX_CHART_POINTS) : points;
  const chartData = series.map((p) => ({
    bucket: domain ? p.bucket : bucketLabel(p.bucket, interval),
    Peak: p.peakConcurrent,
    Average: Math.round(p.avgConcurrent * 10) / 10,
  }));

  return (
    <Paper withBorder p="md">
      <Title order={5} mb="xs">
        Concurrency trend
      </Title>
      {isLoading ? (
        <Skeleton height={220} />
      ) : isError && !noDataYet ? (
        <AnalyticsErrorState error={error} onRetry={onRetry} />
      ) : noDataYet ? (
        <Text size="sm" c="dimmed" py="md">
          {emptyCopy}
        </Text>
      ) : points.length === 0 ? (
        AnalyticsUtils.coverageIsEmpty(concurrency?.coverage, concurrency?.freshness, range) ? (
          <AnalyticsCoverageNotice
            coverage={concurrency?.coverage}
            freshness={concurrency?.freshness}
            range={range}
            emptyMessage="No concurrency data for this range."
          />
        ) : (
          <>
            <AnalyticsCoverageNotice coverage={concurrency?.coverage} freshness={concurrency?.freshness} range={range} />
            <Text size="sm" c="dimmed" py="md">
              {emptyCopy}
            </Text>
          </>
        )
      ) : (
        <>
          <AnalyticsCoverageNotice
            coverage={concurrency?.coverage}
            freshness={concurrency?.freshness}
            range={range}
          />
          <AreaChart
            h={220}
            data={chartData}
            dataKey="bucket"
            series={[
              { name: 'Peak', color: 'blue.6' },
              { name: 'Average', color: 'cyan.4' },
            ]}
            curveType="monotone"
            withDots={false}
            withLegend
            {...(domain
              ? {
                  xAxisProps: {
                    type: 'number' as const,
                    domain: [domain.from, domain.to],
                    ticks: hourTicks(domain),
                    interval: 0 as const,
                    tickFormatter: (v: number) => axisTickLabel(v, multiDay, zone),
                  },
                  tooltipProps: {
                    content: ({ payload }: { payload?: unknown }) => <ConcurrencyTooltip payload={payload} zone={zone} />,
                  },
                }
              : {})}
          />
          <Text size="xs" c="dimmed" mt={4}>
            {interval === 'hour' ? 'Hourly' : 'Per-minute'} peak and average concurrent
            viewers (times shown in your local timezone).
          </Text>
        </>
      )}
    </Paper>
  );
}
